import { useEffect, useId, useRef, useState } from "react";
import { ArrowUpRight, Check, Copy, Download } from "lucide-react";
import type { MapSummary } from "../shared/types";
import apiReferenceUrl from "../../docs/api.md?url";
import openApiUrl from "../../docs/openapi.json?url";
import "./documentation.css";
import { PageHeading } from "./ui";

const sections = [
  ["playground", "Live playground"],
  ["start", "Quick start"],
  ["controls", "Map controls"],
  ["locations", "Booths & pins"],
  ["directions", "Directions"],
  ["events", "Events & lifecycle"],
  ["authoring", "Editing & review"],
  ["http", "HTTP API"],
] as const;

const cameraMethods = [
  [
    "zoomIn() / zoomOut()",
    "Step the zoom in or out, just like the map buttons.",
  ],
  [
    "setZoom(zoom)",
    "Set an absolute zoom from 1 to 4. A zoom of 1 is the fitted view.",
  ],
  [
    "rotate(degrees)",
    "Rotate by a relative amount in degrees. Use a negative amount for the opposite direction.",
  ],
  ["setRotation(degrees)", "Set an absolute rotation in degrees."],
  [
    "setPitch(degrees)",
    "Set the viewing angle from 0 (overhead) to 70 (low angle).",
  ],
  [
    "fit()",
    "Bring the whole map into view while keeping the current rotation and viewing angle.",
  ],
  [
    "fitRoute()",
    "Bring the active route into view while keeping the current rotation and viewing angle. Requires an active route.",
  ],
  [
    "reset()",
    "Restore the published map’s starting rotation and viewing angle, then fit the map.",
  ],
  ["getView()", "Read the current { zoom, rotation, pitch } values."],
] as const;

const events = [
  [
    "event-map:ready",
    "{ mapId, revision }",
    "The published map has loaded and rendered.",
  ],
  [
    "event-map:select",
    "{ mapId, location, pin? }",
    "A location or pin has been selected.",
  ],
  [
    "event-map:viewchange",
    "{ mapId, view: { zoom, rotation, pitch } }",
    "Camera values changed through gestures, built-in controls or your API calls.",
  ],
  [
    "event-map:routechange",
    "{ mapId, route, status }",
    'The active route changed. Status is "ready", "unavailable" or "cleared". Ready describes the route calculation, never a visitor’s arrival.',
  ],
  [
    "event-map:error",
    "{ mapId, code, message }",
    "Loading or rendering failed. Show a useful message and offer a retry where appropriate.",
  ],
] as const;

function CodeExample({ title, code }: { title: string; code: string }) {
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const id = useId();
  useEffect(() => () => clearTimeout(timer.current), []);
  async function copy() {
    clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setNotice(`${title} copied.`);
      timer.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
      setNotice(
        "Copy is unavailable. Select the example and copy it manually.",
      );
    }
  }
  return (
    <div className="em-docs-code">
      <div className="em-docs-code-heading">
        <span id={id}>{title}</span>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={`Copy ${title.toLowerCase()}`}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre tabIndex={0} role="group" aria-labelledby={id}>
        <code>{code}</code>
      </pre>
      <p className="em-docs-copy-notice" role="status" aria-live="polite">
        {notice}
      </p>
    </div>
  );
}

function MethodList({
  methods,
}: {
  methods: readonly (readonly [string, string])[];
}) {
  return (
    <dl className="em-docs-methods">
      {methods.map(([method, description]) => (
        <div key={method}>
          <dt>
            <code>{method}</code>
          </dt>
          <dd>{description}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="em-panel em-docs-section"
      aria-labelledby={`docs-${id}`}
    >
      <h2 id={`docs-${id}`} tabIndex={-1}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Documentation({
  maps,
  initialMapId,
}: {
  maps: MapSummary[];
  initialMapId?: string;
}) {
  const playground = useRef<HTMLIFrameElement>(null);
  const [playgroundHeight, setPlaygroundHeight] = useState<number>();
  const [mapId, setMapId] = useState(
    () =>
      maps.find((map) => map.id === initialMapId)?.id ||
      maps.find((map) => map.publishedRevision !== null)?.id ||
      maps[0]?.id ||
      "your-map",
  );
  useEffect(() => {
    setPlaygroundHeight(undefined);
  }, [mapId]);
  useEffect(() => {
    function resizePlayground(event: MessageEvent) {
      if (
        event.origin !== window.location.origin ||
        event.source !== playground.current?.contentWindow ||
        !event.data ||
        event.data.type !== "event-map:playground-height" ||
        typeof event.data.height !== "number" ||
        !Number.isFinite(event.data.height)
      )
        return;
      setPlaygroundHeight(
        Math.max(400, Math.min(6000, Math.ceil(event.data.height))),
      );
    }
    window.addEventListener("message", resizePlayground);
    return () => window.removeEventListener("message", resizePlayground);
  }, []);
  const origin = window.location.origin;
  const endpoint = `${origin}/api/v1/maps/${encodeURIComponent(mapId)}`;
  const selectedMap = maps.find((map) => map.id === mapId);
  const script = `<div id="venue-map" style="width: 100%; height: 560px;"></div>
<script src="${origin}/embed/v1.js"
  data-map="${mapId}" data-target="#venue-map" defer></script>`;
  const controlled = `<div id="venue-map" style="width: 100%; height: 560px;"></div>
<script src="${origin}/embed/v1.js"></script>
<script type="module">
  const target = document.querySelector("#venue-map");
  const map = await window.GSMAEventMap.mount(target, {
    mapId: ${JSON.stringify(mapId)},
    controls: false
  });

  try {
    await map.ready;
    // Connect your own buttons after the map is ready.
    document.querySelector("#zoom-in")?.addEventListener(
      "click", () => map.zoomIn()
    );
  } catch (error) {
    console.error("The map could not load:", error.message);
  }
</script>`;
  return (
    <div className="em-docs">
      <PageHeading
        title="Documentation"
        description="Embed a venue map. Shape the experience around your page."
        className="em-page-heading"
      />
      <div className="em-docs-intro em-panel">
        <div>
          <span className="em-docs-version">JavaScript API · v1</span>
          <p>
            One map instance, a small set of controls, and events to keep your
            page in sync.
          </p>
        </div>
        <label>
          Map used in examples
          <select
            value={mapId}
            onChange={(event) => setMapId(event.currentTarget.value)}
            disabled={!maps.length}
          >
            {maps.length ? (
              maps.map((map) => (
                <option key={map.id} value={map.id}>
                  {map.title}
                  {map.publishedRevision === null ? " · Draft" : ""}
                </option>
              ))
            ) : (
              <option value="your-map">Your map</option>
            )}
          </select>
        </label>
      </div>
      <div className="em-docs-layout">
        <nav className="em-docs-nav" aria-label="Documentation sections">
          {sections.map(([id, label]) => (
            <button
              type="button"
              key={id}
              onClick={() => {
                const heading = document.getElementById(`docs-${id}`);
                heading?.focus({ preventScroll: true });
                heading?.scrollIntoView({
                  block: "start",
                  behavior: "instant",
                });
              }}
            >
              {label}
            </button>
          ))}
          <a href={openApiUrl} download="event-map-openapi.json">
            <Download size={14} />
            OpenAPI specification
          </a>
        </nav>
        <div className="em-docs-content">
          <Section id="playground" title="Live playground">
            <p>
              Try the map controls here. Each action shows the JavaScript call
              your own page can make. Changes apply to this preview only.
            </p>
            {selectedMap?.publishedRevision === null ? (
              <div className="em-docs-note">
                Publish this map locally to try it in the playground, or choose
                a published map above.
              </div>
            ) : (
              <iframe
                ref={playground}
                className="em-docs-playground"
                style={
                  playgroundHeight
                    ? { height: `${playgroundHeight}px` }
                    : undefined
                }
                title="Map API playground"
                src={`/embed-demo.html?map=${encodeURIComponent(mapId)}&embedded=1`}
                loading="lazy"
              />
            )}
          </Section>
          <Section id="start" title="Quick start">
            <p>
              Give the map a container with a height, then load the script from
              your Gem service. Its styles are isolated from the host page.
              WebGL is used when available, with SVG as the fallback.
            </p>
            {selectedMap?.publishedRevision === null && (
              <div className="em-docs-note">
                This map is a draft. Publish it in this local service before
                trying an embed; publishing locally does not deploy it.
              </div>
            )}
            <CodeExample title="Simple script embed" code={script} />
            <h3>Control it with JavaScript</h3>
            <p>
              Load the script once, then mount each map into its own container.
              Mount attaches the loading interface; await <code>map.ready</code>{" "}
              before using camera or selection methods.
            </p>
            <CodeExample title="JavaScript setup" code={controlled} />
            <p className="em-docs-note">
              Choose either automatic mounting with <code>data-map</code> or{" "}
              <code>GSMAEventMap.mount()</code> for a container. Use{" "}
              <code>controls: false</code> for external controls; pan, pinch,
              double-tap and rotation gestures still work.
            </p>
            <MethodList
              methods={[
                [
                  "mapId",
                  "Required. The stable identifier of a published map.",
                ],
                [
                  "apiBase",
                  "Optional service root URL. Defaults to the origin of the embed script.",
                ],
                ["controls", "Show built-in map controls. Defaults to true."],
                [
                  "pins / route / routeStyle",
                  "Optional starting pins, one active route, and its visual emphasis.",
                ],
                [
                  "onSelect(location, pin?)",
                  "Optional callback for a location or pin selection.",
                ],
                [
                  "onDiagnostic(entry)",
                  "Optional callback for redacted loading and renderer diagnostics. Nothing is sent automatically.",
                ],
                [
                  "nonce",
                  "Optional CSP nonce for generated styles; inherited from the script by default.",
                ],
              ]}
            />
            <p>
              For pages with a Content Security Policy, allow the map service in{" "}
              <code>script-src</code> and <code>connect-src</code>. Logo
              textures also need <code>blob:</code> in <code>img-src</code>.
              Background routing needs <code>worker-src blob:</code>. Pass a
              style nonce when your policy requires one.
            </p>
          </Section>
          <Section id="controls" title="Map controls">
            <p>
              Every view control is available to your page. Camera changes use
              the same limits and behaviour as the built-in controls.
            </p>
            <MethodList methods={cameraMethods} />
            <CodeExample
              title="External map controls"
              code={`await map.ready;
map.zoomIn();
map.zoomOut();
map.setZoom(2);
map.rotate(15);
map.rotate(-15);
map.setRotation(0);
map.setPitch(0);  // Overhead
map.setPitch(70); // Low angle
map.fit();       // Keep the current angles
// With an active route: map.fitRoute();
map.reset();     // Restore the starting view
const view = map.getView();`}
            />
            <p>
              Camera methods called before readiness throw an error with code{" "}
              <code>not_ready</code>. The existing <code>fit()</code> method can
              also be queued during loading.
            </p>
            <p>
              <code>fitRoute()</code> reports <code>no_route</code> if no leg is
              set. If that leg cannot be routed, it leaves the camera in place
              and the route status is <code>unavailable</code>.
            </p>
          </Section>
          <Section id="locations" title="Booths & pins">
            <p>
              Use stable location IDs from the locations endpoint. Selecting a
              booth shows its name, stand number and a thumbnail of its shape.
              Focusing a location brings it into view while preserving your
              viewing angle.
            </p>
            <MethodList
              methods={[
                [
                  "focusLocation(locationId)",
                  "Select and bring a visible location into view. Hidden or unknown locations cannot be focused.",
                ],
                [
                  "clearSelection()",
                  "Clear the selected booth or location and its card.",
                ],
                [
                  "setPins(pins)",
                  "Replace all pins for this instance. Use an empty array to remove them.",
                ],
              ]}
            />
            <CodeExample
              title="Focus a booth and show pins"
              code={`await map.ready;
map.focusLocation("C70");
map.setPins([
  { id: "first-stop", locationId: "C70", number: 1,
    label: "GSMA Pavilion", completed: true },
  { id: "next-stop", locationId: "D40", number: 2,
    label: "Vodafone Qatar" }
]);
// Clear these independently when your page needs to.
map.clearSelection();
map.setPins([]);`}
            />
            <p className="em-docs-note">
              The booth IDs in these examples belong to the Doha demo. Use your
              own map’s IDs. Hidden booths and locations do not appear in
              search, selection or pins. Hiding controls presentation; their
              records remain in public map data.
            </p>
            <p>
              Calling <code>focusLocation()</code> from your page does not fire
              a selection event, so an event handler can focus a booth without
              creating a callback loop.
            </p>
          </Section>
          <Section id="directions" title="Directions">
            <p>
              There is one active route at a time. Each request replaces the
              previous leg. Your page decides the next start and destination;
              the map does not track arrival, GPS position or an itinerary.
            </p>
            <MethodList
              methods={[
                [
                  "setRoute({ from, to })",
                  "Draw one route using location IDs, replacing the previous leg.",
                ],
                [
                  "clearRoute()",
                  "Remove the route and its temporary colour and height emphasis. Keep the current view, pins and selection.",
                ],
                [
                  "setRouteStyle(style)",
                  "Choose the context saturation and endpoint lift for this map instance.",
                ],
              ]}
            />
            <CodeExample
              title="Replace the active route"
              code={`map.setRoute({ from: "C70", to: "D40" });
map.fitRoute(); // Optional: frame this leg at the current angles
// When your page wants the next leg, replace the route.
map.setRoute({ from: "D40", to: "B10" });
// Or go to any other location. No completion step is needed.
map.setRoute({ from: "B10", to: "C70" });
map.clearRoute(); // Remove the current route and its emphasis
// map.setRoute(null) is also supported.`}
            />
            <h3>Emphasise the start and destination</h3>
            <p>
              Soften the other booths, lift the two endpoint booths, combine
              both, or use neither. The lift is temporary and does not change
              the booth heights saved in your map.
            </p>
            <CodeExample
              title="Route appearance"
              code={`map.setRouteStyle({
  contextSaturation: 0.2, // 0 = grey, 1 = full colour
  endpointLift: 8        // 0 = no lift; up to 100 drawing units
});
map.setRoute({ from: "C70", to: "D40" });`}
            />
            <button
              type="button"
              className="em-inline-button"
              onClick={() => {
                const heading = document.getElementById("docs-playground");
                heading?.focus({ preventScroll: true });
                heading?.scrollIntoView({
                  block: "start",
                  behavior: "instant",
                });
              }}
            >
              Try route styling in playground
            </button>
            <p>
              <code>setRouteStyle()</code> replaces the whole style
              configuration. Omitted values use full context colour (
              <code>1</code>) and no lift (<code>0</code>). Use{" "}
              <code>setRouteStyle({"{}"})</code> to restore both defaults.
              Settings apply to this instance and carry into the next leg.
            </p>
            <p>
              Listen for <code>event-map:routechange</code> to distinguish a
              calculated route from an unavailable one. Hidden booth footprints
              remain obstacles even though they are not drawn.
            </p>
            <h3>Control where visitors can walk</h3>
            <p>
              In the editor, open{" "}
              <strong>Locations → Routing &amp; access</strong>
              to draw walkable areas, barriers and doorways on a top-down map.
              Routes follow these saved rules in both renderers. Doorways are
              open unless marked closed. With no walkable areas, the floor
              boundary is used.
            </p>
            <p className="em-docs-note">
              Imported wall and door outlines are reference artwork. They do not
              restrict routes until you add routing rules. Use changes updates
              the draft; save and publish separately, then test the routes in
              this playground.
            </p>
          </Section>
          <Section id="events" title="Events & lifecycle">
            <p>
              Events are dispatched on the map container and bubble across its
              shadow boundary. Attach listeners before mounting to capture
              initial ready, route and error events.
            </p>
            <div className="em-docs-events">
              {events.map(([event, detail, description]) => (
                <div key={event}>
                  <h3>
                    <code>{event}</code>
                  </h3>
                  <code className="em-docs-detail">{detail}</code>
                  <p>{description}</p>
                </div>
              ))}
            </div>
            <CodeExample
              title="Listen to the map"
              code={`const target = document.querySelector("#venue-map");
target.addEventListener("event-map:select", ({ detail }) => {
  console.log("Selected location:", detail.location.id);
});
target.addEventListener("event-map:viewchange", ({ detail }) => {
  console.log("Current view:", detail.view);
});
target.addEventListener("event-map:routechange", ({ detail }) => {
  if (detail.status === "unavailable") {
    console.log("No route is available for this pair.");
  }
});
target.addEventListener("event-map:error", ({ detail }) => {
  console.error(detail.message);
});
const map = await window.GSMAEventMap.mount(target, {
  mapId: ${JSON.stringify(mapId)}
});
await map.ready;`}
            />
            <MethodList
              methods={[
                [
                  "ready",
                  "Promise resolving when the instance has loaded and rendered. It rejects if loading fails; retry creates a new ready promise.",
                ],
                [
                  "retry()",
                  "Retry a failed load using the same handle. Await the returned promise.",
                ],
                [
                  "destroy()",
                  "Release rendering resources, abort pending work and remove only this map’s owned elements. Call this when removing a page or component.",
                ],
                [
                  "getDiagnostics()",
                  "Read a copy of the last 50 redacted lifecycle and renderer diagnostics, including after destruction. Entries contain code, level, message and elapsedMs.",
                ],
              ]}
            />
            <p>
              Invalid arguments throw synchronously; catch errors around calls
              made from user input. Pins, route and route style can be set
              during loading. Each mounted map has its own view, selection and
              route.
            </p>
          </Section>
          <Section id="authoring" title="Editing & review">
            <h3>Add and position destinations</h3>
            <p>
              Choose <strong>Locations → Add place</strong>, give it a name and
              choose its position on the top-down map. For a booth without a
              destination, open{" "}
              <strong>Locations → Pin &amp; navigation</strong>
              and choose <strong>Add navigation point</strong>. Existing points
              can be moved with <strong>Position on map</strong>. These changes
              stay in the draft until you save and publish.
            </p>
            <h3>Recover an edit</h3>
            <p>
              Undo and Redo step through local edits, including earlier save
              points while the editor stays open. Undoing a saved edit creates
              unsaved changes that you can save again. Outside text fields,
              Command/Ctrl+Z undoes and Command/Ctrl+Shift+Z redoes. After a
              reload, the editor can offer unsaved work from the same browser
              session; restoring it requires an explicit choice. Version history
              restores saved content as a new draft while keeping the current
              public map and source connection.
            </p>
            <h3>Review before changing the live map</h3>
            <p>
              Refresh from source first lists added, changed and removed
              records. Apply to draft accepts that exact preview; nothing is
              published. Publish separately reviews the saved draft against the
              public version. If another editor changes the saved map, review
              the latest version before continuing.
            </p>
            <p className="em-docs-note">
              Recovery and editing are separate from publication. Changing a
              draft, restoring a version or editing routing rules does not
              update visitors’ maps until you publish. Publishing in this local
              service does not deploy it.
            </p>
          </Section>
          <Section id="http" title="HTTP API">
            <p>
              Public endpoints return the published snapshot without admin
              credentials. Saving a draft does not update an embed until you
              publish. Both endpoints support cross-origin requests, conditional
              requests with an ETag, and GET or HEAD.
            </p>
            <div className="em-docs-endpoints">
              <a href={endpoint} target="_blank" rel="noreferrer">
                <span>Published map document</span>
                <code>{endpoint}</code>
                <ArrowUpRight size={16} />
              </a>
              <a
                href={`${endpoint}/locations`}
                target="_blank"
                rel="noreferrer"
              >
                <span>Location IDs and labels</span>
                <code>{endpoint}/locations</code>
                <ArrowUpRight size={16} />
              </a>
            </div>
            <p>
              The map response contains{" "}
              <code>{"{ map, revision, publishedAt }"}</code>; locations
              contains <code>{"{ locations, revision }"}</code>. Missing or
              unpublished maps return 404. Public data may be cached for up to
              60 seconds.
            </p>
            <h3>Editing and publishing</h3>
            <p>
              The admin API uses the same organisation authentication as this
              workspace. Viewers can read drafts, editors can edit and publish,
              and owners manage access. Writes require the current user identity
              and revision to protect against conflicting edits.
            </p>
            <p>
              Booth height is set in drawing units: omit it for the category
              default or use zero for a flat booth. Booth and location
              visibility are independent. Hidden records remain in exports and
              API responses.
            </p>
            <div className="em-docs-downloads">
              <a
                className="em-button em-button-secondary"
                href={apiReferenceUrl}
                download="event-map-api.md"
              >
                <Download size={15} />
                HTTP API reference
              </a>
              <a
                className="em-button em-button-secondary"
                href={openApiUrl}
                download="event-map-openapi.json"
              >
                <Download size={15} />
                OpenAPI specification
              </a>
              <a
                className="em-button em-button-plain"
                href={`/embed-demo.html?map=${encodeURIComponent(mapId)}`}
                target="_blank"
                rel="noreferrer"
              >
                Embed playground
                <ArrowUpRight size={15} />
              </a>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
